import React, { useState, useRef } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Link from '@amzn/meridian/link';

export interface FileFieldConfig {
    key: string;
    label: string;
    required?: boolean;
    accept?: string;
}

interface FileUploadModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (files: Record<string, File>) => Promise<void>;
    title: string;
    fields: FileFieldConfig[];
    submitLabel?: string;
    loadingLabel?: string;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
    open, onClose, onSubmit, title, fields,
    submitLabel = 'Continue', loadingLabel = 'Uploading...',
}) => {
    const [files, setFiles] = useState<Record<string, File | null>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

    const requiredMet = fields
        .filter(f => f.required !== false)
        .every(f => files[f.key]);

    const resetInputs = () => {
        Object.values(inputRefs.current).forEach(el => { if (el) el.value = ''; });
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const selected: Record<string, File> = {};
            for (const [k, v] of Object.entries(files)) {
                if (v) selected[k] = v;
            }
            await onSubmit(selected);
            setFiles({});
            resetInputs();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (isSubmitting) return;
        setFiles({});
        resetInputs();
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose} title={title} scrollContainer="modal">
            <Column spacing="400">
                {fields.map(({ key, label, required = true, accept = '.csv,.xlsx' }) => (
                    <Column spacing="200" key={key}>
                        <Text type="b200" tag="label">{label}{required && ' *'}</Text>
                        <input
                            type="file"
                            accept={accept}
                            ref={el => { inputRefs.current[key] = el; }}
                            style={{ display: 'none' }}
                            onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) setFiles(p => ({ ...p, [key]: file }));
                            }}
                        />
                        {files[key] ? (
                            <Row spacing="300" alignmentVertical="center">
                                <Text type="b200">✓ {files[key]!.name}</Text>
                                <Link type="secondary" onClick={() => {
                                    setFiles(p => ({ ...p, [key]: null }));
                                    if (inputRefs.current[key]) inputRefs.current[key]!.value = '';
                                }}>Remove</Link>
                            </Row>
                        ) : (
                            <Button type="secondary" size="small" onClick={() => inputRefs.current[key]?.click()}>
                                Select File
                            </Button>
                        )}
                    </Column>
                ))}
            </Column>
            <ModalFooter>
                <Row alignmentHorizontal="end" spacing="300">
                    <Button type="tertiary" size="small" onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                    <Button type="primary" size="small" onClick={handleSubmit} disabled={!requiredMet || isSubmitting}>
                        {isSubmitting ? loadingLabel : submitLabel}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};

export default FileUploadModal;
